// Registro automático de erros (pedido do titular, 10/10/2026, 2A).
// Quando algo dá errado num aparelho, o app manda ao banco o que a pessoa viu,
// o erro técnico (o mesmo "Detalhe:" das telas de entrada), a tela, a versão e
// o navegador: dá para achar o defeito sem pedir print. Ninguém lê pelo app;
// o titular e o Claude veem pelo GitHub (workflow "erros").
//
// - Vai: console.error(erro) (as telas fazem isso quando não conseguem
//   carregar; "Abertura: …" e "Sincronia: …" dão o lugar), o erro de
//   formulário, a alteração recusada na fila de envio e o erro que escapou
//   (eventos "error" e "unhandledrejection" da janela).
// - Não vai: o que é da pessoa ou do momento (ehErroEsperado, em erros.js) e
//   a falha de rede, a não ser na abertura e na entrada (o caso do iPhone de
//   08/10/2026: o servidor respondia e o app dizia "sem conexão").
// - Nunca vai: o conteúdo dos lançamentos. Os textos passam por limparTexto
//   (sem e-mail, CPF, CNPJ, telefone nem o valor de campos) e a tela vai sem
//   os códigos do endereço (o link do motorista funciona como uma senha).
// - Sem sinal: guarda até 20 no aparelho e manda quando a internet voltar.
// - No máximo 10 por abertura do app, sem repetir o mesmo erro.
import { detalheDoErro, ehErroEsperado, ehFalhaDeRede, mensagemDeErro } from './erros.js';

export const CHAVE_PENDENTES = 'maxcargo-erros-pendentes';
const MAX_PENDENTES = 20;
const MAX_POR_ABERTURA = 10;
// Onde a falha de rede também vale registrar.
const REDE_VALE_EM = new Set(['abertura', 'entrada']);

let ligado = null;
let mandados = new Set();
let quantos = 0;

// Tira do texto o que pode ser dado de alguém.
export function limparTexto(texto, limite = 300) {
  if (texto == null) return null;
  const limpo = String(texto)
    .replace(/\(([^()]*)\)=\([^()]*\)/g, '($1)=(…)')
    .replace(/(syntax for type [\w ]+):.*/gi, '$1: …')
    .replace(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g, '…@…')
    .replace(/\d[\d.\-/ ]{6,}\d/g, '…')
    .replace(/\s+/g, ' ')
    .trim();
  return limpo ? limpo.slice(0, limite) : null;
}

// A tela, sem os códigos do endereço: "#/viagens/3f2a…?ver=40" → "/viagens/:id".
export function telaDoEndereco(hash) {
  const caminho = String(hash ?? '').replace(/^#/, '').split('?')[0];
  if (!caminho || caminho === '/') return '/';
  return caminho.split('/').map((parte) => (parte === '' || /^[a-z][a-z-]{0,19}$/.test(parte) ? parte : ':id')).join('/');
}

// As linhas da pilha (onde estava o código), sem a primeira linha do Chrome,
// que repete a mensagem.
function pilhaDoErro(erro) {
  if (typeof erro?.stack !== 'string') return null;
  const linhas = erro.stack.split('\n').map((l) => l.trim()).filter((l) => /^at\s|@https?:\/\/|@\[native code\]/.test(l));
  return linhas.length ? linhas.slice(0, 4).join(' | ').slice(0, 600) : null;
}

export function montarRegistro(erro, { onde = 'tela', mensagem = null, contexto = {}, versao = null, ambiente = {}, quando = null } = {}) {
  return {
    onde: String(onde).slice(0, 30),
    tela: ambiente.tela ?? null,
    versao,
    mensagem: limparTexto(mensagem ?? mensagemDeErro(erro)),
    detalhe: limparTexto(detalheDoErro(erro)),
    pilha: pilhaDoErro(erro),
    navegador: ambiente.navegador ? String(ambiente.navegador).slice(0, 300) : null,
    instalado: ambiente.instalado ?? null,
    online: ambiente.online ?? null,
    empresa: contexto.empresa ?? null,
    papel: contexto.papel ?? null,
    quando,
  };
}

function ambienteDe(janela) {
  try {
    return {
      tela: telaDoEndereco(janela?.location?.hash),
      navegador: janela?.navigator?.userAgent ?? null,
      instalado: Boolean(janela?.matchMedia?.('(display-mode: standalone)')?.matches || janela?.navigator?.standalone),
      online: janela?.navigator?.onLine ?? null,
    };
  } catch {
    return {};
  }
}

const ehRuido = (erro) => /ResizeObserver loop|^Script error\.?$/i.test(String(erro?.message ?? ''));

export function registrarErro(erro, { onde = 'tela', mensagem = null } = {}) {
  const r = ligado;
  if (!r || r.registrando) return;
  r.registrando = true;
  try {
    if (ehErroEsperado(erro) || ehRuido(erro)) return;
    if (ehFalhaDeRede(erro) && !REDE_VALE_EM.has(onde)) return;
    const registro = montarRegistro(erro, {
      onde, mensagem, contexto: r.contexto() ?? {}, versao: r.versao, ambiente: ambienteDe(r.janela), quando: new Date().toISOString(),
    });
    const chave = `${registro.onde}|${registro.tela}|${registro.detalhe}`;
    if (mandados.has(chave) || quantos >= MAX_POR_ABERTURA) return;
    mandados.add(chave);
    quantos += 1;
    mandar(r, registro);
  } catch {
    // O registro de erros nunca atrapalha o app.
  } finally {
    r.registrando = false;
  }
}

// Sem sinal ou servidor fora: tenta de novo depois. Outro motivo (a função
// ainda não existe no banco, limite): desiste.
const valeTentarDeNovo = (resultado) => Boolean(resultado?.error)
  && (ehFalhaDeRede(resultado.error) || resultado.status === 0 || resultado.status >= 500);

async function enviar(r, registro) {
  try {
    return await r.enviar(registro);
  } catch (erro) {
    return { error: erro, status: 0 };
  }
}

async function mandar(r, registro) {
  const resultado = await enviar(r, registro);
  if (valeTentarDeNovo(resultado)) guardarPendente(r, registro);
  else if (!resultado?.error) await enviarPendentes();
}

function lerPendentes(armazenamento) {
  try {
    const lista = JSON.parse(armazenamento?.getItem(CHAVE_PENDENTES) ?? '[]');
    return Array.isArray(lista) ? lista : [];
  } catch {
    return [];
  }
}

function gravarPendentes(armazenamento, lista) {
  try {
    if (lista.length) armazenamento?.setItem(CHAVE_PENDENTES, JSON.stringify(lista.slice(-MAX_PENDENTES)));
    else armazenamento?.removeItem(CHAVE_PENDENTES);
  } catch {
    // Sem espaço no aparelho: esses se perdem.
  }
}

function guardarPendente(r, registro) {
  gravarPendentes(r.armazenamento, [...lerPendentes(r.armazenamento), registro]);
}

// Manda o que ficou guardado sem sinal (ao abrir o app e quando o sinal volta).
export async function enviarPendentes() {
  const r = ligado;
  if (!r || r.enviandoPendentes) return;
  const lista = lerPendentes(r.armazenamento);
  if (!lista.length) return;
  r.enviandoPendentes = true;
  try {
    gravarPendentes(r.armazenamento, []);
    for (let i = 0; i < lista.length; i += 1) {
      if (valeTentarDeNovo(await enviar(r, lista[i]))) {
        gravarPendentes(r.armazenamento, [...lista.slice(i), ...lerPendentes(r.armazenamento)]);
        break;
      }
    }
  } finally {
    r.enviandoPendentes = false;
  }
}

// Liga o registro (main.js, uma vez). enviar(registro) → { error, status }.
export function ligarRegistroDeErros({
  enviar: enviarAoBanco, contexto = () => ({}), versao = null, janela = globalThis.window, armazenamento = globalThis.localStorage,
}) {
  desligarRegistroDeErros();
  const consoleOriginal = console.error;
  const r = {
    enviar: enviarAoBanco, contexto, versao, janela, armazenamento, consoleOriginal, registrando: false, enviandoPendentes: false,
    aoErro: (evento) => registrarErro(evento?.error ?? evento?.message, { onde: 'janela' }),
    aoRecusar: (evento) => registrarErro(evento?.reason, { onde: 'promessa' }),
    aoVoltarOSinal: () => {
      enviarPendentes();
    },
  };
  ligado = r;
  mandados = new Set();
  quantos = 0;
  console.error = function errosParaORegistro(...args) {
    consoleOriginal.apply(console, args);
    const erro = args.find((a) => a && typeof a === 'object' && ('message' in a || 'code' in a));
    if (!erro) return;
    const lugar = typeof args[0] === 'string' ? /^([A-Za-zÀ-ú]{2,20}):/.exec(args[0])?.[1] : null;
    registrarErro(erro, { onde: lugar ? lugar.toLowerCase() : 'tela' });
  };
  janela?.addEventListener?.('error', r.aoErro);
  janela?.addEventListener?.('unhandledrejection', r.aoRecusar);
  janela?.addEventListener?.('online', r.aoVoltarOSinal);
  enviarPendentes();
}

export function desligarRegistroDeErros() {
  const r = ligado;
  if (!r) return;
  console.error = r.consoleOriginal;
  r.janela?.removeEventListener?.('error', r.aoErro);
  r.janela?.removeEventListener?.('unhandledrejection', r.aoRecusar);
  r.janela?.removeEventListener?.('online', r.aoVoltarOSinal);
  ligado = null;
}
